import { apiClient, getErrorMessage } from './apiClient'
import type { PaginationResponse } from './clienteService'

export interface Usuario {
  id: string
  name: string
  email: string
  role: 'admin' | 'vendedor' | 'operador'
  ativo: boolean
  createdAt: string
  updatedAt: string
}

export interface UsuarioFilters {
  page?: number
  limit?: number
  search?: string
  role?: 'admin' | 'vendedor' | 'operador'
  status?: 'ativo' | 'inativo'
  sort?: string
}

export interface UsuarioCreateRequest {
  name: string
  email: string
  password: string
  role: 'admin' | 'vendedor' | 'operador'
}

export interface UsuarioUpdateRequest {
  name?: string
  email?: string
  password?: string
  role?: 'admin' | 'vendedor' | 'operador'
  ativo?: boolean
}

export interface UsuarioUpdatePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface UsuarioUpdateProfileRequest {
  name?: string
  email?: string
}

export const usuarioService = {
  async getAll(filters?: UsuarioFilters): Promise<PaginationResponse<Usuario>> {
    try {
      const params = new URLSearchParams()
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())
      if (filters?.search) params.append('search', filters.search)
      if (filters?.role) params.append('role', filters.role)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.sort) params.append('sort', filters.sort)

      const response = await apiClient.get<PaginationResponse<Usuario>>(`/usuarios?${params}`)
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getById(id: string): Promise<Usuario> {
    try {
      const response = await apiClient.get<Usuario>(`/usuarios/${id}`)
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getProfile(): Promise<Usuario> {
    try {
      const response = await apiClient.get<Usuario>('/usuarios/me')
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async create(data: UsuarioCreateRequest): Promise<Usuario> {
    try {
      const response = await apiClient.post<Usuario>('/auth/register', data)
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async update(id: string, data: UsuarioUpdateRequest): Promise<Usuario> {
    try {
      console.log('Atualizando usuário:', id)
      console.log('Dados enviados:', { ...data, password: data.password ? '***' : undefined })
      
      const response = await apiClient.put<Usuario>(`/usuarios/${id}`, data)
      console.log('Usuário atualizado com sucesso')
      return response.data
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error)
      throw new Error(getErrorMessage(error))
    }
  },

  async updateProfile(data: UsuarioUpdateProfileRequest): Promise<Usuario> {
    try {
      const response = await apiClient.put<Usuario>('/usuarios/me', data)
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async updatePassword(data: UsuarioUpdatePasswordRequest): Promise<void> {
    try {
      console.log('Enviando alteração de senha para:', '/usuarios/me/password')
      console.log('Dados (mascarados):', { ...data, currentPassword: '***', newPassword: '***' })
      
      // Tenta com PATCH primeiro (mais comum para updates parciais)
      try {
        const response = await apiClient.patch('/usuarios/me/password', data)
        console.log('Resposta PATCH:', response.status, response.data)
        return
      } catch (patchError: any) {
        console.log('PATCH falhou, tentando PUT...', patchError?.response?.status)
        
        // Se PATCH falhar, tenta PUT
        if (patchError?.response?.status === 404 || patchError?.response?.status === 405) {
          const response = await apiClient.put('/usuarios/me/password', data)
          console.log('Resposta PUT:', response.status, response.data)
          return
        }
        
        // Se não for erro de método não permitido, relança o erro
        throw patchError
      }
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error)
      console.error('Response data:', error?.response?.data)
      console.error('Response status:', error?.response?.status)
      throw new Error(getErrorMessage(error))
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`/usuarios/${id}`)
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },
}
